import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mailbox, ExternalLink, Copy, Check, Loader2, ShieldCheck, XCircle } from "lucide-react";

const ENTRA_ROLES_URL = "https://entra.microsoft.com/#view/Microsoft_AAD_IAM/RolesManagementMenuBlade/~/AllRoles";

export function ExoRoleGuide({ open, onOpenChange, appId, onVerified }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState(null);
  const [autoDetecting, setAutoDetecting] = useState(false);

  // Background auto-detect: while the guide is open and not yet verified, poll the lightweight
  // Graph-only role-status so the panel flips to success on its own once the role propagates.
  useEffect(() => {
    if (!open || result?.ok) { setAutoDetecting(false); return; }
    let cancelled = false;
    setAutoDetecting(true);
    const poll = async () => {
      try {
        const { data } = await api.get("/settings/exo/role-status");
        if (!cancelled && data?.roleReady) {
          setResult({ ok: true, message: t("exoGuide.autoDetected") });
          setAutoDetecting(false);
          toast.success(t("exoGuide.autoDetected"));
          onVerified && onVerified();
        }
      } catch { /* keep polling */ }
    };
    poll();
    const id = setInterval(poll, 6000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, result?.ok]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(appId || "");
      setCopied(true);
      toast.success(t("exoGuide.copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const verify = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/settings/exo/test");
      setResult({ ok: true, message: data?.message || t("set.saasExoRoleOk") });
      toast.success(data?.message || t("set.saasExoRoleOk"));
      onVerified && onVerified();
    } catch (e) {
      const m = apiErr(e);
      setResult({ ok: false, message: m });
      toast.error(m);
    } finally { setBusy(false); }
  };

  const steps = ["exoGuide.step1", "exoGuide.step2", "exoGuide.step3", "exoGuide.step4"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="exo-role-guide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-black uppercase tracking-tight">
            <Mailbox size={20} /> {t("exoGuide.title")}
          </DialogTitle>
          <DialogDescription>{t("exoGuide.optionalNote")}</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-zinc-600">{t("exoGuide.intro")}</p>

        <ol className="space-y-2" data-testid="exo-guide-steps">
          {steps.map((s, i) => (
            <li key={s} className="flex items-start gap-3 text-sm text-zinc-800">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-bold text-white">{i + 1}</span>
              <span className="pt-0.5">{t(s)}</span>
            </li>
          ))}
        </ol>

        <a href={ENTRA_ROLES_URL} target="_blank" rel="noreferrer" data-testid="exo-guide-open-entra"
          className="inline-flex w-fit items-center gap-2 rounded-sm bg-primary px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-[#002480]">
          <ExternalLink size={15} /> {t("exoGuide.openEntra")}
        </a>

        {appId && (
          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="exo-guide-appid">
            <div className="text-xs font-semibold text-zinc-500">{t("exoGuide.appLabel")}</div>
            <div className="mt-1 flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate font-mono text-sm text-zinc-900" data-testid="exo-guide-appid-value">{appId}</code>
              <Button type="button" variant="outline" size="sm" onClick={copyId} data-testid="exo-guide-copy-btn" className="rounded-sm border-zinc-300">
                {copied ? <Check size={14} className="mr-1.5 text-emerald-600" /> : <Copy size={14} className="mr-1.5" />} {t("exoGuide.copy")}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div data-testid="exo-guide-result" className={`flex items-start gap-2 rounded-md border p-3 text-xs ${result.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`}>
            {result.ok ? <ShieldCheck size={15} className="mt-0.5 shrink-0" /> : <XCircle size={15} className="mt-0.5 shrink-0" />}
            <p className="font-medium">{result.message}</p>
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-2">
          {autoDetecting && !result?.ok && (
            <span className="mr-auto inline-flex items-center gap-1.5 text-xs text-zinc-500" data-testid="exo-guide-autodetect">
              <Loader2 size={13} className="animate-spin" /> {t("exoGuide.autoDetecting")}
            </span>
          )}
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="exo-guide-close-btn">
            {t("exoGuide.close")}
          </Button>
          <Button type="button" onClick={verify} disabled={busy} data-testid="exo-guide-verify-btn" className="rounded-sm">
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <ShieldCheck size={15} className="mr-1.5" />}
            {busy ? t("exoGuide.verifying") : t("exoGuide.verify")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
